import { useRef, useCallback } from "react";

/**
 * Hook for detecting when users dismiss the Google reCAPTCHA v2 Invisible challenge
 * Based on the working implementation from LoginOtpWithMutationObserver.jsx
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onDismissed - Callback when dismissal is detected
 * @param {number} options.setupDelay - Delay before setting up observer (default: 500ms)
 * @returns {Object} - Object with setupDismissalListener and cleanup functions
 */
export const useRecaptchaDismissalDetector = ({ onDismissed = () => {}, setupDelay = 500 } = {}) => {
  const observerRef = useRef(null);
  const timeoutRef = useRef(null);

  const cleanup = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const setupDismissalListener = useCallback(() => {
    // Clean up any existing observer
    cleanup();

    const checkForChallenge = setTimeout(() => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "attributes" && mutation.attributeName === "style") {
            const target = mutation.target;

            // Look for the reCAPTCHA bframe iframe
            const iframe =
              target.querySelector?.('iframe[src*="google.com/recaptcha/api2/bframe"]') ||
              target.closest?.('iframe[src*="google.com/recaptcha/api2/bframe"]');

            if (iframe) {
              const container = iframe.parentElement?.parentElement;

              // Check if container is hidden (dismissed)
              if (container && (container.style.opacity === "0" || container.style.visibility === "hidden")) {
                // Verify no response token was obtained
                const response = window.grecaptcha?.getResponse?.();
                if (!response) {
                  // User dismissed without completing
                  onDismissed();

                  // Reset reCAPTCHA and cleanup
                  window.grecaptcha?.reset?.();
                  observer.disconnect();
                  observerRef.current = null;
                }
              }
            }
          }
        });
      });

      // Observe DOM changes for style attribute modifications
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style"]
      });

      observerRef.current = observer;
    }, setupDelay);

    timeoutRef.current = checkForChallenge;

    // Return cleanup function
    return cleanup;
  }, [onDismissed, setupDelay, cleanup]);

  return {
    setupDismissalListener,
    cleanup
  };
};
